import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Skeleton } from '@mui/material';
import { useFetchPublicHaveListQuery } from 'store/services/collection/publicCollectionApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import './ProfileHaveListTab.css';

interface ProfileHaveListTabProps {
  username: string;
}

// Get card image URL with fallback
const getCardImageUrl = (item: CollectionList): string => {
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;

  if (item.card?.imageUris?.normal) {
    return item.card.imageUris.normal;
  }
  if (item.card?.imageUris?.small) {
    return item.card.imageUris.small;
  }
  if (item.card?.imageUris?.large) {
    return item.card.imageUris.large;
  }
  if (item.card?.normal_fallbacks?.[0]) {
    return item.card.normal_fallbacks[0];
  }

  return defaultImage;
};

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Card item component
const HaveListCardItem: React.FC<{ item: CollectionList }> = ({ item }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;
  const imageUrl = getCardImageUrl(item);

  const cardName = item.card?.name || 'Unknown Card';

  const handleClick = () => {
    const scryfallId = item.card?.scryfallID;
    if (scryfallId) {
      navigate(`/cards/${scryfallId}`);
    }
  };

  return (
    <div className="profile-have-card-item" onClick={handleClick}>
      <div className="profile-have-card-image-container">
        <img
          src={imageError ? defaultImage : imageUrl}
          alt={cardName}
          className="profile-have-card-image"
          onError={() => setImageError(true)}
          loading="lazy"
        />
        {item.quantity > 1 && (
          <div className="profile-have-card-quantity">x{item.quantity}</div>
        )}
        {item.foil && <div className="profile-have-card-foil">FOIL</div>}
      </div>
      <div className="profile-have-card-info">
        <span className="profile-have-card-name" title={cardName}>
          {cardName}
        </span>
        {item.displayPrice != null && (
          <Points points={item.displayPrice} type="small" />
        )}
      </div>
    </div>
  );
};

// Loading skeleton
const HaveListSkeleton: React.FC = () => (
  <div className="profile-have-skeleton">
    <div className="profile-have-header">
      <Skeleton variant="text" width={120} height={28} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
    </div>
    <div className="profile-have-grid">
      {[...Array(12)].map((_, i) => (
        <div key={i} className="profile-have-card-item">
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              aspectRatio: '488 / 680',
              borderRadius: '8px',
            }}
          />
          <Skeleton variant="text" width="80%" height={16} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        </div>
      ))}
    </div>
  </div>
);

// Private have list message
const PrivateHaveListMessage: React.FC = () => (
  <div className="profile-have-private">
    <div className="profile-have-private-icon">&#128274;</div>
    <h3 className="profile-have-private-title">Have List is Private</h3>
    <p className="profile-have-private-description">
      This user has chosen to keep their have list private.
    </p>
  </div>
);

export const ProfileHaveListTab: React.FC<ProfileHaveListTabProps> = ({ username }) => {
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);

  const { data, isLoading, isFetching, error } = useFetchPublicHaveListQuery({
    username,
    page,
    limit: 48,
  });

  // Check if it's a 403 forbidden error (private have list)
  const isPrivate = error && typeof error === 'object' && 'status' in error && error.status === 403;

  // When loading more pages, accumulate previous results
  const handleLoadMore = () => {
    if (data?.haveList) {
      setPreviousPages((prev) => [...prev, ...data.haveList]);
    }
    setPage((p) => p + 1);
  };

  // Reset when username changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
  }, [username]);

  // Handle private have list
  if (isPrivate) {
    return <PrivateHaveListMessage />;
  }

  if (isLoading) {
    return <HaveListSkeleton />;
  }

  // Combine previous pages with current page data
  const allItems = [...previousPages, ...(data?.haveList || [])];

  if (!allItems.length) {
    return (
      <div className="profile-have-empty">
        <p>This user's have list is empty.</p>
      </div>
    );
  }

  const totalCards = data?.totalCards || 0;
  const totalValue = data?.totalValue || 0;
  const hasMore = data?.hasMore || false;

  return (
    <div className="profile-have-tab">
      <div className="profile-have-header">
        <span className="profile-have-count">{formatNumber(totalCards)} Cards</span>
        {totalValue > 0 && <Points points={totalValue} />}
      </div>

      <div className="profile-have-grid">
        {allItems.map((item) => (
          <HaveListCardItem key={item.id} item={item} />
        ))}
      </div>

      {hasMore && (
        <div className="profile-have-load-more">
          <MagicButton
            label={isFetching ? 'Loading...' : 'Load More'}
            color="dark-grey"
            onPress={handleLoadMore}
            isLoading={isFetching}
            isDisabled={isFetching}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileHaveListTab;
